// Regras PURAS da página de boas-vindas do Nexo (funcionários). Sem React/router e sem IA/serviço externo:
// as saudações são frases cadastradas aqui. Mantido testável de forma determinística — o relógio e o
// localStorage entram por parâmetro/funções próprias, não dentro das funções puras.
import type { Account, AccountRole } from "@/lib/types";
import { hasPermission, homeRouteFor, isStaffRole } from "@/lib/types";
import { buildVisibleNav, isGroup, type NavItem } from "@/layouts/adminNav";
import type { ComponentType } from "react";

/** Rota da nova tela inicial dos funcionários (home limpa, fora do sistema de abas). */
export const STAFF_HOME_ROUTE = "/admin/boas-vindas";

export type DayPeriod = "morning" | "afternoon" | "evening";

/** Faixa do dia por hora (0–23), no fuso de Brasília: <12 manhã; 12–17 tarde; >=18 noite. */
export function periodForHour(hour: number): DayPeriod {
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

/** Hora (0–23) no fuso de Brasília (America/Sao_Paulo), independente do fuso do dispositivo. */
export function brasiliaHour(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const hour = parts.find((p) => p.type === "hour")?.value ?? "0";
  return Number.parseInt(hour, 10);
}

// Saudações cadastradas. Cada período tem a abertura ("Bom dia"...) e vários corpos (variações) para evitar
// repetição. Regras de conteúdo: não presumir que a pessoa almoçou; à noite, não incentivar trabalhar além
// do horário — apenas acolher e lembrar de pausas/descanso.
const SALUTATION: Record<DayPeriod, string> = {
  morning: "Bom dia",
  afternoon: "Boa tarde",
  evening: "Boa noite",
};

const BODIES: Record<DayPeriod, string[]> = {
  morning: [
    "Que seu dia seja maravilhoso. Vamos começar?",
    "Pronto para mais um dia? Vamos juntos.",
    "Que bom ter você aqui. Por onde começamos?",
  ],
  afternoon: [
    "Que bom ter você por aqui. Como está seu dia?",
    "Seguimos juntos nesta tarde. Como posso ajudar?",
    "Boa tarde de trabalho. O que vamos resolver agora?",
  ],
  evening: [
    "Ainda por aqui? Espero que esteja tudo bem. Lembre de fazer uma pausa.",
    "Que bom ver você. Vá com calma e descanse quando puder.",
    "Por aqui a esta hora? Cuide de você e faça pausas quando precisar.",
  ],
};

/** Quantidade de variações de corpo para um período (para quem precisa percorrer sem repetir). */
export function greetingVariantCount(period: DayPeriod): number {
  return BODIES[period].length;
}

// Rotação da variação por navegador: a cada nova saudação avança 1, para não repetir a mesma frase em
// aberturas consecutivas. Só avança quando uma saudação NOVA é montada (não a cada render/retorno de aba).
const ROTATION_KEY = "nexoGreetingRotation";

export function nextGreetingRotation(): number {
  if (typeof window === "undefined") return 0;
  const current = Number.parseInt(window.localStorage.getItem(ROTATION_KEY) ?? "0", 10);
  const safe = Number.isFinite(current) ? current : 0;
  window.localStorage.setItem(ROTATION_KEY, String(safe + 1));
  return safe;
}

/**
 * Monta a saudação completa de forma determinística. `name` ausente/vazio → saudação natural, sem nome
 * ("Bom dia! ..."). `variant` escolhe a frase (percorre em módulo para variar e evitar repetição).
 */
export function pickGreeting(period: DayPeriod, name: string | null | undefined, variant: number): string {
  const bodies = BODIES[period];
  const body = bodies[((variant % bodies.length) + bodies.length) % bodies.length];
  const clean = (name ?? "").trim();
  const opening = clean ? `${SALUTATION[period]}, ${clean}!` : `${SALUTATION[period]}!`;
  return `${opening} ${body}`;
}

/**
 * Primeiro nome exibível do funcionário. Prioriza o nome do próprio cadastro de staff (`account.name`,
 * exposto aditivamente em /auth/me); se ausente, cai no nome de contato do perfil comercial (titular/cliente).
 * Sem fonte confiável → null (saudação natural sem nome). NÃO deriva nome do e-mail (evita apelidos
 * incorretos).
 */
export function staffFirstName(account: Account | null | undefined): string | null {
  const source = account?.name?.trim() || account?.profile?.contactName?.trim();
  if (!source) return null;
  const first = source.split(/\s+/)[0];
  return first || null;
}

// --- Dashboards operacionais (os 4 painéis sob /dashboard/*), em ordem de prioridade. ---
const DASHBOARDS: { path: string; anyOf: string[] }[] = [
  { path: "/dashboard/operacoes", anyOf: ["PEDIDOS.PEDIDO.VISUALIZAR"] },
  { path: "/dashboard/compras", anyOf: ["PEDIDOS.COMPRA.AVANCAR"] },
  { path: "/dashboard/producao", anyOf: ["PEDIDOS.PRODUCAO.AVANCAR", "PEDIDOS.PRODUCAO.IN_NATURA"] },
  { path: "/dashboard/expedicao", anyOf: ["PEDIDOS.EXPEDICAO.AVANCAR"] },
];

/** Dashboard permitido ao usuário (o primeiro na ordem) ou null se nenhum painel é acessível. */
export function primaryDashboardFor(account: Account | null | undefined): string | null {
  for (const d of DASHBOARDS) {
    if (d.anyOf.some((p) => hasPermission(account, p))) return d.path;
  }
  return null;
}

// --- Preferência "usar meu Dashboard como página inicial", por usuário, só neste navegador. ---
// Chaveada pelo ID ESTÁVEL da conta (UUID), não pelo e-mail (que pode mudar) — assim a preferência não se
// perde ao trocar o e-mail e não mistura contas.
export type StaffHomePreference = "welcome" | "dashboard";

function prefKey(accountId: string): string {
  return `nexoStaffHome:${accountId}`;
}

/** Lê a preferência de página inicial do usuário pelo ID da conta (default: boas-vindas). */
export function getStaffHomePreference(accountId: string | null | undefined): StaffHomePreference {
  if (!accountId || typeof window === "undefined") return "welcome";
  return window.localStorage.getItem(prefKey(accountId)) === "dashboard" ? "dashboard" : "welcome";
}

/** Grava a preferência de página inicial do usuário pelo ID da conta (não mistura contas). */
export function setStaffHomePreference(accountId: string | null | undefined, pref: StaffHomePreference): void {
  if (!accountId || typeof window === "undefined") return;
  window.localStorage.setItem(prefKey(accountId), pref);
}

/**
 * Rota de ENTRADA no ambiente (pós-login, "/", "/admin"). Preserva clientes/entregadores (delega a
 * homeRouteFor). Para funcionários de back-office: respeita a preferência — se "dashboard" e houver um
 * painel permitido, entra no Dashboard; caso contrário (e por padrão), abre a tela de boas-vindas. Nunca
 * redireciona para uma tela operacional arbitrária quando não há Dashboard permitido.
 */
export function staffEntryRoute(account: Account | null | undefined, supplierNfEnabled = true): string {
  const role: AccountRole | undefined = account?.role;
  const isBackOffice = isStaffRole(role) && role !== "ENTREGADOR";
  if (!isBackOffice) return homeRouteFor(account, supplierNfEnabled);
  if (getStaffHomePreference(account?.id) === "dashboard") {
    const dash = primaryDashboardFor(account);
    if (dash) return dash;
  }
  return STAFF_HOME_ROUTE;
}

/** Atalho exibido na página de boas-vindas (rótulo já desambiguado quando há colisão de nome). */
export type WelcomeShortcut = { to: string; label: string; icon: ComponentType<{ className?: string }> };

/**
 * Atalhos (itens de navegação permitidos) para a página de boas-vindas — mesma fonte que a sidebar. Alguns
 * rótulos se repetem entre menus diferentes (ex.: "Produtos" em Catálogo e em Relatórios, "Operações" no
 * Dashboard e avulsa). Para não confundir, rótulos repetidos são qualificados com o grupo de origem
 * ("Produtos · Relatórios"); rótulos únicos ficam como estão.
 */
export function welcomeShortcuts(
  account: Account | null | undefined,
  supplierNfEnabled: boolean,
): WelcomeShortcut[] {
  const entries = buildVisibleNav(account, supplierNfEnabled);
  const items: { item: NavItem; group: string | null }[] = [];
  for (const entry of entries) {
    if (isGroup(entry)) entry.items.forEach((item) => items.push({ item, group: entry.label }));
    else items.push({ item: entry, group: null });
  }
  const labelCounts = new Map<string, number>();
  for (const { item } of items) labelCounts.set(item.label, (labelCounts.get(item.label) ?? 0) + 1);
  return items.map(({ item, group }) => ({
    to: item.to,
    icon: item.icon,
    label: group && (labelCounts.get(item.label) ?? 0) > 1 ? `${item.label} · ${group}` : item.label,
  }));
}
